import { useState } from "react";
import { X, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";

/* -------------------------------------------------------
   Fixed Login Credentials
------------------------------------------------------- */

const FIXED_USERNAME = "BiomedicalWaste";
const FIXED_PASSWORD = "Mams$135";

/* -------------------------------------------------------
   Login Component
------------------------------------------------------- */

const Login = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  /* Login fields */
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  /* Messages */
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) {
    return null;
  }

  /* -------------------------------------------------------
     LOGIN
  ------------------------------------------------------- */

  const handleLogin = (e) => {
    e.preventDefault();

    setError("");

    const enteredUsername = username.trim();

    /* Username validation */
    if (!enteredUsername) {
      setError("Please enter your username.");
      return;
    }

    /* Password validation */
    if (!password) {
      setError("Please enter your password.");
      return;
    }

    setIsLoading(true);

    /* Fixed username + password check */
    if (
      enteredUsername === FIXED_USERNAME &&
      password === FIXED_PASSWORD
    ) {
      /* Store login status */
      localStorage.setItem(
        "mams_logged_in_user",
        JSON.stringify({
          username: FIXED_USERNAME,
        })
      );

      /* Close login popup */
      onClose();

      /* Redirect to Biomedical Waste page */
      setTimeout(() => {
        navigate("/biomedicalwaste");
      }, 200);

      return;
    }

    /* Invalid credentials */
    setError("Invalid username or password.");
    setIsLoading(false);
  };

  /* -------------------------------------------------------
     RENDER
  ------------------------------------------------------- */

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 px-4 py-6"
      onClick={onClose}
    >
      {/* Modal */}
      <div
        className="relative w-full max-w-[500px] rounded-2xl bg-white px-6 py-8 shadow-2xl sm:px-9 sm:py-9"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-5 top-5 flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100 hover:text-gray-800"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Logo */}
        <div className="flex justify-center">
          <img
            src="/MAMS-Logo.png"
            alt="MAMS Hospitals Logo"
            className="h-auto w-[150px] object-contain"
          />
        </div>

        {/* Login Heading */}
        <h2 className="mt-6 text-center text-2xl font-semibold text-gray-900">
          Login
        </h2>

        <p className="mt-3 text-center text-[15px] leading-6 text-gray-500">
          Enter your username and password to continue.
        </p>

        {/* Login Form */}
        <form
          onSubmit={handleLogin}
          className="mt-8"
        >
          {/* Username */}
          <label
            htmlFor="username"
            className="mb-2 block text-[16px] font-medium text-gray-700"
          >
            Username
          </label>

          <input
            id="username"
            type="text"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              setError("");
            }}
            placeholder="Enter username"
            autoComplete="username"
            className="h-[56px] w-full rounded-xl border border-gray-300 px-4 text-[16px] text-gray-800 outline-none transition focus:border-[#126B70] focus:ring-1 focus:ring-[#126B70]"
          />

          {/* Password */}
          <label
            htmlFor="password"
            className="mb-2 mt-6 block text-[16px] font-medium text-gray-700"
          >
            Password
          </label>

          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError("");
              }}
              placeholder="Enter password"
              autoComplete="current-password"
              className="h-[56px] w-full rounded-xl border border-gray-300 px-4 pr-12 text-[16px] text-gray-800 outline-none transition focus:border-[#126B70] focus:ring-1 focus:ring-[#126B70]"
            />

            {/* Show / Hide Password */}
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#126B70]"
              aria-label={
                showPassword
                  ? "Hide password"
                  : "Show password"
              }
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Login Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="mt-6 h-[52px] w-full rounded-xl bg-[#126B70] text-[16px] font-semibold text-white transition hover:bg-[#0D5559] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLoading ? "Logging in..." : "Login"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
